import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CalendarDays, Plus, X, Clock, User, AlertTriangle } from 'lucide-react';

const STATUS_COLORS = {
  SCHEDULED: 'bg-blue-100 text-blue-700',
  CONFIRMED: 'bg-emerald-100 text-emerald-700',
  COMPLETED: 'bg-slate-100 text-slate-600',
  NO_SHOW: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-slate-100 text-slate-500',
  REBOOKED: 'bg-violet-100 text-violet-700',
};

export default function Appointments() {
  const { api } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ lead_id: '', date_time: '', provider: '', notes: '' });

  useEffect(() => {
    async function load() {
      try {
        const [aRes, lRes] = await Promise.all([
          api().get('/api/appointments'),
          api().get('/api/leads', { params: { limit: 200 } }),
        ]);
        setAppointments(aRes.data);
        setLeads(lRes.data.leads || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, [api]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api().post('/api/appointments', form);
      setShowAdd(false);
      setForm({ lead_id: '', date_time: '', provider: '', notes: '' });
      const { data } = await api().get('/api/appointments');
      setAppointments(data);
    } catch (e) { console.error(e); }
  };

  const handleNoShow = async (id) => {
    try {
      await api().patch(`/api/appointments/${id}/no-show`);
      const { data } = await api().get('/api/appointments');
      setAppointments(data);
    } catch (e) { console.error(e); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api().patch(`/api/appointments/${id}`, { status });
      const { data } = await api().get('/api/appointments');
      setAppointments(data);
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="appointments-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Appointments</h1>
          <p className="text-sm text-slate-500 mt-0.5">{appointments.length} total appointments</p>
        </div>
        <button data-testid="appt-add-btn" onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all">
          <Plus size={16} /> New Appointment
        </button>
      </div>

      {appointments.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <CalendarDays size={40} className="mx-auto text-slate-300 mb-4" />
          <p className="text-lg font-semibold text-slate-900">No appointments yet</p>
          <p className="text-sm text-slate-500 mt-2">Create your first appointment to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-6 py-3">Patient</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-6 py-3">Date & Time</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-6 py-3">Provider</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-6 py-3">Status</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {appointments.map(appt => {
                const lead = leads.find(l => l.id === appt.lead_id);
                return (
                  <tr key={appt.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-slate-900">
                        {lead ? `${lead.first_name} ${lead.last_name}` : 'Unknown'}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-700 flex items-center gap-1.5">
                        <Clock size={14} className="text-slate-400" />
                        {new Date(appt.date_time).toLocaleString()}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-600 flex items-center gap-1.5">
                        <User size={14} className="text-slate-400" />
                        {appt.provider || '—'}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[appt.status] || 'bg-slate-100 text-slate-600'}`}>
                        {appt.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        {appt.status === 'SCHEDULED' && (
                          <>
                            <button onClick={() => updateStatus(appt.id, 'CONFIRMED')} className="text-xs text-emerald-600 hover:underline">Confirm</button>
                            <button onClick={() => handleNoShow(appt.id)} className="text-xs text-red-600 hover:underline flex items-center gap-1">
                              <AlertTriangle size={10} /> No Show
                            </button>
                          </>
                        )}
                        {appt.status === 'CONFIRMED' && (
                          <>
                            <button onClick={() => updateStatus(appt.id, 'COMPLETED')} className="text-xs text-emerald-600 hover:underline">Complete</button>
                            <button onClick={() => handleNoShow(appt.id)} className="text-xs text-red-600 hover:underline flex items-center gap-1">
                              <AlertTriangle size={10} /> No Show
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-900">New Appointment</h2>
              <button onClick={() => setShowAdd(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Patient *</label>
                <select data-testid="appt-lead-select" required value={form.lead_id} onChange={e => setForm(p => ({ ...p, lead_id: e.target.value }))}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                  <option value="">Select patient...</option>
                  {leads.map(l => <option key={l.id} value={l.id}>{l.first_name} {l.last_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Date & Time *</label>
                <input data-testid="appt-datetime" type="datetime-local" required value={form.date_time}
                  onChange={e => setForm(p => ({ ...p, date_time: e.target.value }))}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Provider</label>
                <input data-testid="appt-provider" value={form.provider} onChange={e => setForm(p => ({ ...p, provider: e.target.value }))}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="Dr. Smith" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
                <textarea data-testid="appt-notes" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none" />
              </div>
              <button data-testid="appt-submit" type="submit"
                className="w-full bg-slate-900 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all">
                Create Appointment
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
